import { useEffect, useState, useRef, useMemo } from 'react';
import { FieldDictionary } from '@shopify/react-form';
import type { IdField } from '@sdk/types';
import type { AuthUserType } from '@sdk/authentication/types/AuthUserType';

import isValidName from '@web/onboarding/Checklist/AddEmployeesModal/validateName';
import { LAST_NAME_REQUIRED_TEXT } from '@web/onboarding/Checklist/AddEmployeesModal/constants';

import type { AddPersonaCollectionUser } from '../onboardingChecklist.types';

import type { Employee } from './addEmployeesModal.types';
import { getDefaultEmployees } from './addEmployeesModal.utils';
import { useAddEmployeesForm } from './addEmployeesModal.form';

type HookProps = {
  locations: IdField[];
  currentUser: AuthUserType;
  addPersonaCollectionUser: AddPersonaCollectionUser;
  close: () => void;
  onCloseCallback?: () => void;
};

const useAddEmployeesModal = ({
  locations,
  currentUser,
  addPersonaCollectionUser,
  close,
}: HookProps) => {
  const focusFieldRef = useRef(null);
  const config = useRef({ noDismiss: true }).current;
  const defaultEmployees = useMemo(() => getDefaultEmployees(locations), [locations]);

  const {
    fields,
    addItem: addEmployeeField,
    submit,
    errors,
    setErrors,
    reset,
  } = useAddEmployeesForm(defaultEmployees, currentUser, addPersonaCollectionUser);
  const [hasSuggestedInvite, setHasSuggestedInvite] = useState(false);

  useEffect(() => {
    const hasEmail = fields.employees.some(({ email }) => email.value.trim());
    if (!hasSuggestedInvite && hasEmail) {
      setHasSuggestedInvite(true);
      fields.shouldSendInvite.onChange(true);
    }
  }, [fields, hasSuggestedInvite]);

  const onShouldSendInviteChange = (value: boolean) => {
    fields.shouldSendInvite.onChange(value);
    setHasSuggestedInvite(true);
  };

  const onFullNameChange = (value: string, field: FieldDictionary<Employee>, index: number) => {
    if (!isValidName(value)) {
      field.fullName.setError(LAST_NAME_REQUIRED_TEXT);
    }
    if (field.fullName.error) {
      field.fullName.reset();
    }
    field.fullName.onChange(value);
    const isLastEmployeeField = index === fields.employees.length - 1;
    if (value.trim() && isLastEmployeeField) {
      addEmployeeField();
    }
  };

  const submitHandler = async (): Promise<void> => {
    await submit();
    reset();
    close();
  };

  const closeHandler = () => {
    reset();
    setErrors([]);
    close();
  };

  return {
    focusFieldRef,
    config,
    fields,
    errors,
    onShouldSendInviteChange,
    onFullNameChange,
    submitHandler,
    closeHandler,
  };
};

export default useAddEmployeesModal;
